import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Tabs, Tab, Container, Paper, useTheme, useMediaQuery, Menu, MenuItem } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LinkIcon from '@mui/icons-material/Link';
import PaymentsIcon from '@mui/icons-material/Payments';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// --- Styled Components for Premium Look ---

const StyledPaper = styled(Paper)(({ theme }) => ({
    backgroundColor: 'rgba(255, 255, 255, 0.85)', // Glass effect base
    backdropFilter: 'blur(12px)', // Frosted glass
    borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.05)',
    position: 'sticky',
    top: 0,
    zIndex: 1100, // Higher than standard content
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    transition: 'all 0.3s ease-in-out',
}));

const StyledTabs = styled(Tabs)({
    '& .MuiTabs-indicator': {
        display: 'none', // Hide default indicator
    },
    '& .MuiTabs-flexContainer': {
        gap: '12px',
        justifyContent: 'center', // Center tabs by default
    },
});

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
    textTransform: 'none',
    minWidth: 0,
    [theme.breakpoints.up('sm')]: {
        minWidth: 100,
    },
    fontWeight: 700,
    fontSize: '1.05rem',
    color: '#636E72', // Neutral text
    padding: '12px 20px',
    borderRadius: '50px', // Pill shape
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        color: '#BD1F34',
        backgroundColor: 'rgba(189, 31, 52, 0.08)',
        transform: 'translateY(-1px)',
    },
    '&.Mui-selected': {
        color: '#fff',
        backgroundColor: '#BD1F34', // Brand Red
        boxShadow: '0 4px 14px 0 rgba(189, 31, 52, 0.39)', // Glowing shadow
        '&:hover': {
            backgroundColor: '#a01a2c',
        }
    },
    '& .MuiSvgIcon-root': {
        marginBottom: '0 !important',
        marginRight: theme.spacing(1),
        fontSize: '1.2rem',
    },
}));

export default function NavigationMenu() {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const rol = localStorage.getItem('rol');
    const { t } = useTranslation("global");

    // Menu State
    const [anchorEl, setAnchorEl] = useState(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const openMenu = Boolean(anchorEl);

    // Hierarchical Menu Structure
    const menuStructure = [
        {
            id: 'ordenes',
            label: t('menu.ordenes'),
            icon: <ShoppingCartIcon />,
            isDropdown: false,
            value: '/proveedores/órdenes'
        },
        {
            id: 'finanzas',
            label: t('menu_categorias.finanzas'),
            icon: <PaymentsIcon />,
            isDropdown: true,
            options: [
                { label: t('menu.pagos'), value: '/proveedores/pagos' },
                { label: t('menu.estadodecuenta'), value: '/proveedores/Estado de cuenta' },
                // { label: t('certificados.titulo'), value: '/proveedores/certificados' },
                // { label: "Certificados SAP (Prueba)", value: '/proveedores/certificados-sap' }
            ]
        },
        {
            id: 'documentos',
            label: t('menu_categorias.documentos'),
            icon: <FolderIcon />,
            isDropdown: true,
            options: [
                { label: t('menu.documentacion'), value: '/proveedores/documentación' },
                { label: t('sst_content.menu_titulo'), value: '/proveedores/sst-upload' },
            ]
        }
    ];

    if (rol === 'Admin' || rol === 'ADMIN') {
        menuStructure.push({
            id: 'evaluaciones',
            label: t('menu.evaluaciones'),
            icon: <AssessmentIcon />,
            isDropdown: true,
            options: [
                { label: t('evaluacion.visita_esp'), value: '/proveedores/visitaproveedores' },
                { label: t('evaluacion.visita_eng'), value: '/proveedores/visitaproveedoresen' },
                { label: t('evaluacion.calidad_esp'), value: '/proveedores/visitacalidad' },
                { label: t('evaluacion.calidad_eng'), value: '/proveedores/visitacalidaden' },
            ]
        });

        menuStructure.push({
            id: 'configuracion',
            label: t('menu_categorias.administracion'),
            icon: <AdminPanelSettingsIcon />,
            isDropdown: true,
            options: [
                { label: t('menu.generarlinkproveedores'), value: '/proveedores/generar link proveedores' },
                { label: t('menu.configuracion'), value: '/proveedores/configuracion' }
            ]
        });
    }

    const normalizeUrl = (url) => decodeURIComponent(url).toLowerCase().trim();
    const currentPath = normalizeUrl(location.pathname);

    // Determine Logic for Active Tab
    let activeTab = false;

    for (const group of menuStructure) {
        if (group.isDropdown) {
            const isMatch = group.options.some(opt => normalizeUrl(opt.value) === currentPath || currentPath.startsWith(normalizeUrl(opt.value)));
            if (isMatch) {
                activeTab = group.id;
                break;
            }
        } else {
            if (normalizeUrl(group.value) === currentPath || currentPath.startsWith(normalizeUrl(group.value))) {
                activeTab = group.id || group.value;
                break;
            }
        }
    }

    const handleChange = (event, newValue) => {
        const group = menuStructure.find(g => (g.id || g.value) === newValue);
        if (group && group.isDropdown) {
            setAnchorEl(event.currentTarget);
            setOpenDropdownId(group.id);
        } else if (group) {
            navigate(group.value);
        }
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setOpenDropdownId(null);
    };

    const handleMenuClick = (path) => {
        navigate(path);
        handleMenuClose();
    };

    // Get currently open dropdown options
    const currentDropdownGroup = menuStructure.find(g => g.id === openDropdownId);
    const dropdownOptions = currentDropdownGroup ? currentDropdownGroup.options : [];

    return (
        <StyledPaper elevation={0}>
            <Container maxWidth="xl">
                <Box sx={{ width: '100%' }}>
                    <StyledTabs
                        value={activeTab}
                        onChange={handleChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        aria-label="navigation menu"
                        sx={{
                            '& .MuiTabs-scrollButtons': {
                                color: '#BD1F34',
                            }
                        }}
                    >
                        {menuStructure.map((item) => (
                            <StyledTab
                                key={item.id || item.value}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {item.label}
                                        {item.isDropdown && <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: '1rem !important', mr: '0 !important' }} />}
                                    </Box>
                                }
                                value={item.id || item.value}
                                icon={item.icon}
                                iconPosition="start"
                                onClick={(e) => {
                                    if (item.isDropdown) {
                                        setAnchorEl(e.currentTarget);
                                        setOpenDropdownId(item.id);
                                    }
                                }}
                            />
                        ))}
                    </StyledTabs>

                    {/* Dropdown Menu */}
                    <Menu
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={handleMenuClose}
                        PaperProps={{
                            elevation: 3,
                            sx: {
                                mt: 1.5,
                                borderRadius: 3,
                                minWidth: 200,
                                '& .MuiMenuItem-root': {
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    py: 1.5,
                                    borderRadius: 1,
                                    mb: 0.5,
                                    mx: 0.5,
                                    '&:hover': {
                                        backgroundColor: 'rgba(189, 31, 52, 0.08)',
                                        color: '#BD1F34',
                                    }
                                }
                            }
                        }}
                        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                    >
                        {dropdownOptions.map((option) => (
                            <MenuItem
                                key={option.value}
                                onClick={() => handleMenuClick(option.value)}
                                selected={normalizeUrl(option.value) === currentPath}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>
            </Container>
        </StyledPaper>
    );
}
